import { useEffect, useState, useMemo } from 'react'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

// /admin/logistica/medidas-envio
// Bulk-edit de peso + dimensiones + bolsa recomendada para todos los productos.
// Los datos los consume el wizard ML (Step 9 envío) para calcular tarifa Mercado Envíos.

export default function MedidasEnvio() {
  const [productos, setProductos] = useState([])
  const [bolsas, setBolsas] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtro, setFiltro] = useState('')
  const [filtroEstado, setFiltroEstado] = useState('todos') // todos | faltan | completos
  const [editando, setEditando] = useState({}) // id -> dirty fields
  const [guardando, setGuardando] = useState(null)
  const [msg, setMsg] = useState(null)

  async function cargar() {
    setLoading(true)
    const [prodRes, bolsasRes] = await Promise.all([
      supabase
        .from('productos')
        .select('id, sku, nombre, marca, presentacion, categoria, peso_g, largo_cm, ancho_cm, alto_cm, bolsa_recomendada_id, activo')
        .is('deleted_at', null)
        .eq('activo', true)
        .order('marca')
        .order('nombre'),
      supabase.from('v_bolsas_envio').select('*').order('capacidad_max_kg'),
    ])
    setProductos(prodRes.data || [])
    setBolsas(bolsasRes.data || [])
    setLoading(false)
  }

  useEffect(() => { cargar() }, [])

  // Sugerencia automática de bolsa según peso/volumen
  function sugerirBolsa(p) {
    const pesoKg = (p.peso_g || 0) / 1000
    const litros = ((p.largo_cm || 0) * (p.ancho_cm || 0) * (p.alto_cm || 0)) / 1000
    const chica = bolsas.find(b => b.sku === 'PACK_CHICO')
    const grande = bolsas.find(b => b.sku === 'PACK_GRANDE')
    if (!chica && !grande) return null
    if (chica && pesoKg <= chica.capacidad_max_kg && litros <= chica.capacidad_max_litros) return chica.id
    if (grande && pesoKg <= grande.capacidad_max_kg && litros <= grande.capacidad_max_litros) return grande.id
    return grande?.id || chica?.id || null
  }

  const productosFiltrados = useMemo(() => {
    return productos.filter(p => {
      if (filtro && !`${p.sku} ${p.nombre} ${p.marca}`.toLowerCase().includes(filtro.toLowerCase())) return false
      const completo = p.peso_g && p.largo_cm && p.ancho_cm && p.alto_cm && p.bolsa_recomendada_id
      if (filtroEstado === 'faltan' && completo) return false
      if (filtroEstado === 'completos' && !completo) return false
      return true
    })
  }, [productos, filtro, filtroEstado])

  function setField(id, field, value) {
    setProductos(ps => ps.map(p => p.id === id ? { ...p, [field]: value === '' ? null : value } : p))
    setEditando(e => ({ ...e, [id]: { ...(e[id] || {}), [field]: true } }))
  }

  async function guardarFila(p) {
    setGuardando(p.id)
    setMsg(null)
    try {
      const bolsa = p.bolsa_recomendada_id || sugerirBolsa(p)
      const update = {
        peso_g: p.peso_g ? Number(p.peso_g) : null,
        largo_cm: p.largo_cm ? Number(p.largo_cm) : null,
        ancho_cm: p.ancho_cm ? Number(p.ancho_cm) : null,
        alto_cm: p.alto_cm ? Number(p.alto_cm) : null,
        bolsa_recomendada_id: bolsa,
      }
      const { error } = await supabase.from('productos').update(update).eq('id', p.id)
      if (error) throw error
      setEditando(e => { const c = { ...e }; delete c[p.id]; return c })
      setProductos(ps => ps.map(x => x.id === p.id ? { ...x, ...update } : x))
      setMsg({ ok: true, txt: `✓ ${p.nombre}` })
      setTimeout(() => setMsg(null), 1500)
    } catch (e) {
      setMsg({ ok: false, txt: e.message || String(e) })
    } finally {
      setGuardando(null)
    }
  }

  // Detectar productos del mismo grupo (marca + categoria + presentacion) que aún no tienen medidas
  function similaresSinMedidas(p) {
    if (!p.marca || !p.categoria || !p.presentacion) return []
    return productos.filter(x =>
      x.id !== p.id
      && x.marca === p.marca
      && x.categoria === p.categoria
      && x.presentacion === p.presentacion
      && (!x.peso_g || !x.largo_cm || !x.ancho_cm || !x.alto_cm)
    )
  }

  const [propagando, setPropagando] = useState(false)
  async function propagarTodoMasivo() {
    const sinMedidas = productos.filter(p => !(p.peso_g && p.largo_cm && p.ancho_cm && p.alto_cm))
    if (sinMedidas.length === 0) {
      setMsg({ ok: true, txt: 'Todos los productos ya tienen medidas cargadas.' })
      return
    }
    if (!confirm(`Buscar productos sin medidas que tengan hermanos cargados (mismo marca + categoría + presentación) y copiarles las medidas en bulk.\n\n¿Continuamos?`)) return
    setPropagando(true)
    setMsg(null)
    try {
      const { data, error } = await supabase.rpc('admin_propagar_medidas_a_hermanos')
      if (error) throw error
      setMsg({ ok: true, txt: `✓ ${data?.mensaje || 'Propagación completada'}` })
      await cargar()
    } catch (e) {
      setMsg({ ok: false, txt: 'Error: ' + (e?.message || String(e)) })
    } finally {
      setPropagando(false)
    }
  }

  async function copiarASimilares(p) {
    const similares = similaresSinMedidas(p)
    if (similares.length === 0) return
    if (!confirm(`Copiar peso ${p.peso_g}g + dimensiones ${p.largo_cm}×${p.ancho_cm}×${p.alto_cm} cm + bolsa a ${similares.length} producto${similares.length > 1 ? 's' : ''} similar${similares.length > 1 ? 'es' : ''}?\n\n${similares.slice(0, 5).map(x => '• ' + x.nombre).join('\n')}${similares.length > 5 ? `\n... y ${similares.length - 5} más` : ''}`)) return
    setGuardando(p.id)
    setMsg(null)
    try {
      const update = {
        peso_g: p.peso_g ? Number(p.peso_g) : null,
        largo_cm: p.largo_cm ? Number(p.largo_cm) : null,
        ancho_cm: p.ancho_cm ? Number(p.ancho_cm) : null,
        alto_cm: p.alto_cm ? Number(p.alto_cm) : null,
        bolsa_recomendada_id: p.bolsa_recomendada_id || sugerirBolsa(p),
      }
      const ids = similares.map(x => x.id)
      const { error } = await supabase.from('productos').update(update).in('id', ids)
      if (error) throw error
      setProductos(ps => ps.map(x => ids.includes(x.id) ? { ...x, ...update } : x))
      setMsg({ ok: true, txt: `✓ Copiado a ${similares.length} producto${similares.length > 1 ? 's' : ''} similar${similares.length > 1 ? 'es' : ''}` })
      setTimeout(() => setMsg(null), 2500)
    } catch (e) {
      setMsg({ ok: false, txt: e.message || String(e) })
    } finally {
      setGuardando(null)
    }
  }

  const stats = useMemo(() => {
    const total = productos.length
    const completos = productos.filter(p => p.peso_g && p.largo_cm && p.ancho_cm && p.alto_cm && p.bolsa_recomendada_id).length
    return { total, completos, faltan: total - completos }
  }, [productos])

  return (
    <AdminLayout title="Medidas de envío — Logística">
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '24px 20px' }}>
        <div style={{ marginBottom: 18, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 280 }}>
            <h1 style={{ fontSize: 24, fontWeight: 800, margin: 0, letterSpacing: '-0.02em' }}>Medidas de envío</h1>
            <p style={{ fontSize: 13.5, color: '#64748B', margin: '6px 0 0' }}>
              Peso, dimensiones y bolsa recomendada por producto. Lo usa el wizard ML para calcular tarifa Mercado Envíos.
            </p>
          </div>
          <button
            onClick={propagarTodoMasivo}
            disabled={propagando}
            title="Para cada producto con medidas cargadas, copia los valores a sus hermanos (mismo marca + categoría + presentación, ej. el mismo envase en otro aroma)"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: 'linear-gradient(135deg, #00A8B5, #1A3461)',
              color: '#fff', border: 'none',
              padding: '10px 18px', borderRadius: 10,
              fontSize: 13, fontWeight: 700, cursor: propagando ? 'wait' : 'pointer',
              opacity: propagando ? 0.6 : 1, whiteSpace: 'nowrap',
              boxShadow: '0 4px 12px rgba(0,168,181,0.25)',
            }}
          >
            {propagando ? 'Propagando…' : '✨ Propagar a hermanos'}
          </button>
        </div>

        {/* KPIs */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 16 }}>
          <div style={{ background: '#fff', border: '1px solid rgba(26,52,97,0.1)', borderRadius: 8, padding: '12px 14px' }}>
            <div style={{ fontSize: 11, color: '#7888A3', letterSpacing: 0.6, textTransform: 'uppercase', fontWeight: 600 }}>Productos</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#1A3461', marginTop: 4 }}>{stats.total}</div>
          </div>
          <div style={{ background: '#fff', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 8, padding: '12px 14px' }}>
            <div style={{ fontSize: 11, color: '#7888A3', letterSpacing: 0.6, textTransform: 'uppercase', fontWeight: 600 }}>Completos</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#065F46', marginTop: 4 }}>{stats.completos}</div>
          </div>
          <div style={{ background: '#fff', border: '1px solid rgba(245,158,11,0.35)', borderRadius: 8, padding: '12px 14px' }}>
            <div style={{ fontSize: 11, color: '#7888A3', letterSpacing: 0.6, textTransform: 'uppercase', fontWeight: 600 }}>Faltan datos</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#854F0B', marginTop: 4 }}>{stats.faltan}</div>
          </div>
          <div style={{ background: '#fff', border: '1px solid rgba(26,52,97,0.1)', borderRadius: 8, padding: '12px 14px' }}>
            <div style={{ fontSize: 11, color: '#7888A3', letterSpacing: 0.6, textTransform: 'uppercase', fontWeight: 600 }}>Bolsas activas</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#1A3461', marginTop: 4 }}>{bolsas.length}</div>
          </div>
        </div>

        {/* Filtros */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Buscar SKU, nombre, marca…"
            value={filtro}
            onChange={e => setFiltro(e.target.value)}
            style={{ flex: '1 1 240px', padding: '8px 12px', border: '1px solid rgba(26,52,97,0.15)', borderRadius: 8, fontSize: 13, fontFamily: 'inherit' }}
          />
          <select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid rgba(26,52,97,0.15)', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', background: '#fff' }}>
            <option value="faltan">Solo los que faltan datos</option>
            <option value="completos">Solo completos</option>
            <option value="todos">Todos</option>
          </select>
        </div>

        {msg && (
          <div style={{
            background: msg.ok ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
            border: `1px solid ${msg.ok ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
            borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 12.5,
            color: msg.ok ? '#065F46' : '#991B1B',
          }}>{msg.txt}</div>
        )}

        {loading ? (
          <p style={{ color: '#64748B' }}>Cargando…</p>
        ) : (
          <div style={{ background: '#fff', border: '1px solid rgba(26,52,97,0.1)', borderRadius: 10, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 1100 }}>
              <thead style={{ background: '#F1F5F9', color: '#1A3461', fontWeight: 700, fontSize: 11, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                <tr>
                  <th style={{ padding: '10px 12px', textAlign: 'left' }}>SKU</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left' }}>Producto</th>
                  <th style={{ padding: '10px 8px', textAlign: 'right', width: 90 }}>Peso (g)</th>
                  <th style={{ padding: '10px 8px', textAlign: 'right', width: 75 }}>Largo</th>
                  <th style={{ padding: '10px 8px', textAlign: 'right', width: 75 }}>Ancho</th>
                  <th style={{ padding: '10px 8px', textAlign: 'right', width: 75 }}>Alto</th>
                  <th style={{ padding: '10px 8px', textAlign: 'left', width: 130 }}>Bolsa</th>
                  <th style={{ padding: '10px 8px', textAlign: 'center', width: 90 }}></th>
                </tr>
              </thead>
              <tbody>
                {productosFiltrados.map(p => {
                  const dirty = !!editando[p.id]
                  const completo = p.peso_g && p.largo_cm && p.ancho_cm && p.alto_cm && p.bolsa_recomendada_id
                  const bolsaSugerida = sugerirBolsa(p)
                  return (
                    <tr key={p.id} style={{ borderTop: '1px solid rgba(26,52,97,0.06)', background: dirty ? 'rgba(255,225,150,0.15)' : (completo ? 'transparent' : 'rgba(245,158,11,0.04)') }}>
                      <td style={{ padding: '6px 12px', color: '#7888A3', fontFamily: 'monospace', fontSize: 11 }}>{p.sku}</td>
                      <td style={{ padding: '6px 12px', color: '#1A3461' }}>
                        <div style={{ fontWeight: 600 }}>{p.nombre}</div>
                        <div style={{ fontSize: 11, color: '#7888A3', marginTop: 1 }}>{p.marca} · {p.presentacion}</div>
                      </td>
                      <td style={{ padding: '4px 6px' }}>
                        <input type="number" step="1" value={p.peso_g || ''} onChange={e => setField(p.id, 'peso_g', e.target.value)}
                          style={inputStyle} placeholder="—" />
                      </td>
                      <td style={{ padding: '4px 6px' }}>
                        <input type="number" step="0.1" value={p.largo_cm || ''} onChange={e => setField(p.id, 'largo_cm', e.target.value)}
                          style={inputStyle} placeholder="cm" />
                      </td>
                      <td style={{ padding: '4px 6px' }}>
                        <input type="number" step="0.1" value={p.ancho_cm || ''} onChange={e => setField(p.id, 'ancho_cm', e.target.value)}
                          style={inputStyle} placeholder="cm" />
                      </td>
                      <td style={{ padding: '4px 6px' }}>
                        <input type="number" step="0.1" value={p.alto_cm || ''} onChange={e => setField(p.id, 'alto_cm', e.target.value)}
                          style={inputStyle} placeholder="cm" />
                      </td>
                      <td style={{ padding: '4px 6px' }}>
                        <select value={p.bolsa_recomendada_id || ''} onChange={e => setField(p.id, 'bolsa_recomendada_id', e.target.value)}
                          style={{ ...inputStyle, textAlign: 'left', paddingLeft: 6 }}>
                          <option value="">{bolsaSugerida ? '↻ Auto' : '—'}</option>
                          {bolsas.map(b => <option key={b.id} value={b.id}>{b.sku === 'PACK_CHICO' ? 'Chica' : 'Grande'}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                        {dirty && (
                          <button onClick={() => guardarFila(p)} disabled={guardando === p.id}
                            title="Guardar esta fila"
                            style={{ background: '#1A3461', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: guardando ? 'wait' : 'pointer' }}>
                            {guardando === p.id ? '…' : '💾'}
                          </button>
                        )}
                        {completo && !dirty && (() => {
                          const n = similaresSinMedidas(p).length
                          return (
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <span style={{ color: '#10B981', fontWeight: 700 }}>✓</span>
                              {n > 0 && (
                                <button onClick={() => copiarASimilares(p)} disabled={guardando === p.id}
                                  title={`Copiar a ${n} producto${n > 1 ? 's' : ''} similar${n > 1 ? 'es' : ''} sin medidas`}
                                  style={{ background: 'rgba(0,168,181,0.15)', color: '#00A8B5', border: '1px solid rgba(0,168,181,0.4)', padding: '3px 8px', borderRadius: 6, fontSize: 10.5, fontWeight: 700, cursor: guardando ? 'wait' : 'pointer' }}>
                                  📋 ×{n}
                                </button>
                              )}
                            </div>
                          )
                        })()}
                      </td>
                    </tr>
                  )
                })}
                {productosFiltrados.length === 0 && (
                  <tr><td colSpan={8} style={{ padding: 20, textAlign: 'center', color: '#7888A3' }}>No hay productos con esos filtros.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ marginTop: 16, padding: '12px 16px', background: 'rgba(0,168,181,0.06)', border: '1px solid rgba(0,168,181,0.2)', borderRadius: 8, fontSize: 12, color: '#1A3461', lineHeight: 1.6 }}>
          <strong>Cómo medir:</strong> el peso es del envase lleno tal como sale del depósito. Las dimensiones son del producto (no la bolsa). La bolsa recomendada se sugiere automáticamente según capacidad — si dejás "↻ Auto", el wizard ML elige sola al publicar.<br/>
          <strong>Bolsas disponibles:</strong> Chica 50×60 (46g · hasta 3L / 4kg) · Grande 66×100 (90g · hasta 10L / 12kg).<br/>
          <strong>📋 Copiar a similares:</strong> al guardar un producto que tiene "hermanos" con misma marca + categoría + presentación (ej. Ecume 270mL viene en 4 aromas), aparece el botón <code style={{ background: 'rgba(0,168,181,0.1)', color: '#00A8B5', padding: '1px 5px', borderRadius: 3, fontSize: 11 }}>📋 ×N</code> al final de la fila. Click → copia peso/dim/bolsa a los hermanos sin medidas. Cargás 1 vez, propagás a todo el grupo.
        </div>
      </div>
    </AdminLayout>
  )
}

const inputStyle = {
  width: '100%',
  padding: '5px 6px',
  border: '1px solid rgba(26,52,97,0.15)',
  borderRadius: 5,
  fontSize: 12,
  fontFamily: 'inherit',
  textAlign: 'right',
  background: '#fff',
  color: '#1A3461',
}
